import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";

export default function TermsOfService() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const updated = "July 2, 2026";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-emerald-600 hover:text-emerald-700 mb-6">
          <ArrowLeft className="w-4 h-4" />Back to Home
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <div className="p-2.5 rounded-xl bg-emerald-100 dark:bg-emerald-900/40">
            <FileText className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Terms of Service</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Last updated: {updated}</p>
          </div>
        </div>

        <div className="prose prose-sm sm:prose dark:prose-invert max-w-none space-y-6">
          <section>
            <p>
              Welcome to the Grow Sport Psychology & Mental Performance Improvement platform (the &ldquo;Platform&rdquo;). 
              These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and use of the Platform provided by 
              Grow Sport Psychology & Mental Performance Improvement (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;).
            </p>
            <p>By creating an account or using the Platform, you agree to be bound by these Terms. If you do not agree, please do not use the Platform.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">1. Eligibility</h2>
            <p>
              You must be at least 13 years of age to use the Platform. If you are under 18, you represent that 
              you have obtained parental or guardian consent to use the Platform and agree to these Terms. By 
              using the Platform, you represent and warrant that you meet these eligibility requirements.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">2. Account Registration</h2>
            <ul>
              <li>You must provide accurate and complete information when creating an account.</li>
              <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
              <li>You are responsible for all activities that occur under your account.</li>
              <li>You must notify us immediately of any unauthorized use of your account.</li>
              <li>We reserve the right to suspend or terminate accounts that violate these Terms.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">3. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Platform for any unlawful purpose or in violation of any applicable laws or regulations.</li>
              <li>Attempt to gain unauthorized access to other users&apos; accounts or data.</li>
              <li>Interfere with or disrupt the Platform&apos;s infrastructure or other users&apos; experience.</li>
              <li>Upload malicious code, viruses, or any harmful content.</li>
              <li>Use automated scripts, bots, or scrapers to access the Platform without our prior written consent.</li>
              <li>Impersonate any person or entity, or falsely represent your affiliation with any person or entity.</li>
              <li>Share your account credentials with others or allow others to access the Platform through your account.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">4. Platform Services</h2>
            <p>The Platform provides mental performance training tools for athletes, including but not limited to:</p>
            <ul>
              <li>AI-generated personalized training plans and exercise recommendations.</li>
              <li>Guided mental training exercises, breathing techniques, and visualization practices.</li>
              <li>Daily wellness check-ins and performance trend tracking.</li>
              <li>AI-powered coaching conversations and journal prompts.</li>
              <li>Pre-game routine builders and daily mental warmup sessions.</li>
              <li>Guided audio sessions with text-to-speech narration.</li>
              <li>Goal setting and progress tracking tools.</li>
              <li>Team management and plan sharing features.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">5. AI-Generated Content Disclaimer</h2>
            <p><strong>Important:</strong> The Platform uses artificial intelligence to generate training plans, coaching advice, and other content. You acknowledge and agree that:</p>
            <ul>
              <li>AI-generated content is for <strong>informational and educational purposes only</strong>.</li>
              <li>AI-generated content does <strong>not</strong> constitute professional medical advice, psychological counseling, therapy, or diagnosis.</li>
              <li>AI-generated content should not be used as a substitute for consultation with qualified healthcare professionals, licensed psychologists, or certified sport psychology consultants.</li>
              <li>We do not guarantee the accuracy, completeness, or suitability of any AI-generated content for your specific circumstances.</li>
              <li>You use AI-generated content at your own discretion and risk.</li>
              <li>If you are experiencing a mental health crisis, please contact a qualified professional or emergency services immediately.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">6. Intellectual Property</h2>
            <ul>
              <li>The Platform, including its design, code, features, content, and branding, is owned by us and protected by intellectual property laws.</li>
              <li>You retain ownership of the content you create on the Platform (journal entries, notes, goals, routines).</li>
              <li>By using the Platform, you grant us a limited, non-exclusive license to store, process, and display your content as necessary to provide the services.</li>
              <li>AI-generated training plans and content created for you through the Platform are provided for your personal use.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">7. Shared Content and Team Features</h2>
            <ul>
              <li>When you share a training plan with another user, you grant them read access to the shared content.</li>
              <li>You can revoke shared plan access at any time.</li>
              <li>If you participate in team features, your coach or team administrator may have access to certain aggregated performance metrics. Your private journal entries and personal notes are never shared.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">8. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, GROW SPORT PSYCHOLOGY & MENTAL PERFORMANCE IMPROVEMENT 
              SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, 
              INCLUDING BUT NOT LIMITED TO LOSS OF DATA, LOSS OF PROFITS, OR PERSONAL INJURY, ARISING OUT OF 
              OR RELATED TO YOUR USE OF THE PLATFORM.
            </p>
            <p>
              THE PLATFORM IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo; WITHOUT WARRANTIES OF ANY KIND, WHETHER 
              EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS 
              FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">9. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless Grow Sport Psychology & Mental Performance Improvement, 
              its officers, directors, employees, and agents from and against any claims, liabilities, damages, 
              losses, or expenses arising from your use of the Platform, your violation of these Terms, or your 
              violation of any rights of another.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">10. Termination</h2>
            <ul>
              <li>You may stop using the Platform at any time by closing your account.</li>
              <li>We may suspend or terminate your access at any time, with or without cause, with or without notice.</li>
              <li>Upon termination, your right to use the Platform ceases immediately.</li>
              <li>You may request deletion of your data by contacting us at the email address below.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">11. Modifications to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify you of any material changes 
              by posting updated Terms on the Platform with a revised &ldquo;Last Updated&rdquo; date. Your continued use of 
              the Platform after changes are posted constitutes your acceptance of the revised Terms.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">12. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the United States, 
              without regard to conflict of law principles. Any disputes arising from these Terms shall be 
              resolved through good-faith negotiation or, if necessary, in the courts of competent jurisdiction.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">13. Contact Us</h2>
            <p>If you have questions about these Terms of Service, please contact us at:</p>
            <p>
              <strong>Grow Sport Psychology & Mental Performance Improvement</strong><br />
              Email: <a href="mailto:sports@growwellnessgroup.com" className="text-emerald-600 hover:text-emerald-700">sports@growwellnessgroup.com</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
